/**
 * Audio player hook for TTS voice clips.
 *
 * Playback runs through the Web Audio engine (`./engine.ts`). The iOS silent
 * switch would mute it; `./silentSwitch.ts` (installed in main.tsx) stops that.
 *
 * Public surface:
 *   • useAudio(key) → { play, stop, playing, error, retry }
 *   • preloadAudio(key) — fire-and-forget warm-up
 *
 * (The slow-replay 🐢 feature was removed 2026-06-12 — Tornike: not needed.)
 *
 * Failure model:
 *   - Network/decode/playback errors mark the hook's `error` state. The
 *     caller decides what to render (we show a small retry button).
 *   - retry() forces a fresh download (bypasses the HTTP + buffer cache).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  __resetEngineForTests,
  type PlayHandle,
  playClip,
  preloadClip,
} from './engine.js';

/** Warm up the buffer cache for an audio_key. Safe to call repeatedly. */
export function preloadAudio(audioKey: string | undefined | null) {
  preloadClip(audioKey);
}

export interface UseAudioState {
  /** Plays the clip at normal speed. Restarts from 0 if already playing. */
  play: () => void;
  /** Stops the currently-playing clip and resets it. */
  stop: () => void;
  /** True while the clip is audibly playing. */
  playing: boolean;
  /** True if the most recent playback attempt failed (network / decode). */
  error: boolean;
  /** Force a fresh fetch (bypasses cache) on the next play. */
  retry: () => void;
}

const NO_OP: UseAudioState = {
  play: () => {},
  stop: () => {},
  playing: false,
  error: false,
  retry: () => {},
};

export function useAudio(audioKey: string | undefined | null): UseAudioState {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  const handleRef = useRef<PlayHandle | null>(null);
  const reloadRef = useRef(false);

  // If the audio_key changes, stop our clip and reset state.
  useEffect(() => {
    return () => {
      handleRef.current?.stop();
      handleRef.current = null;
    };
  }, [audioKey]);

  const stop = useCallback(() => {
    handleRef.current?.stop();
    handleRef.current = null;
    setPlaying(false);
  }, []);

  const play = useCallback(() => {
    if (!audioKey) return;
    // Stop our own previous handle; the engine also supersedes globally.
    handleRef.current?.stop();
    setError(false);
    setPlaying(true);
    const reload = reloadRef.current;
    reloadRef.current = false;
    handleRef.current = playClip(audioKey, {
      reload,
      onEnded: () => {
        setPlaying(false);
        handleRef.current = null;
      },
      onError: () => {
        setPlaying(false);
        setError(true);
        handleRef.current = null;
      },
    });
  }, [audioKey]);

  const retry = useCallback(() => {
    reloadRef.current = true;
    setError(false);
    play();
  }, [play]);

  if (!audioKey) return NO_OP;
  return { play, stop, playing, error, retry };
}

/** Test-only — reset module state between vitest runs. */
export function __resetAudioForTests() {
  __resetEngineForTests();
}
